import { Fragment } from 'react';

/**
 * Renders the legal documents' Markdown.
 *
 * Deliberately tiny, and not a Markdown library: these documents use four
 * constructs and nothing else, and pulling a parser in for that would add
 * weight to every bundle for no gain. It handles exactly:
 *
 *   # Heading          document title
 *   ## Heading         section
 *   - item             bullet
 *   **bold**           inline emphasis
 *
 * Before this, both surfaces split the text on newlines and emitted a <p> per
 * line. That worked while the documents were plain text; against Markdown it
 * would have printed "## 5. Community Rules" and "- Harassment" to the reader,
 * characters and all.
 */

/** Split a line on **bold** runs, keeping the bold parts marked. */
function inline(text: string, keyPrefix: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, i) => (
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={`${keyPrefix}-${i}`} className="font-semibold text-white">{part.slice(2, -2)}</strong>
      : <Fragment key={`${keyPrefix}-${i}`}>{part}</Fragment>
  ));
}

export function LegalMarkdown({ content }: { content: string }) {
  const lines = content.split('\n');
  const out: React.ReactNode[] = [];
  let bullets: string[] = [];

  // Bullets arrive as consecutive lines and have to become one <ul>, so they
  // are buffered and flushed when the run ends.
  const flushBullets = (key: string) => {
    if (bullets.length === 0) return;
    out.push(
      <ul key={`ul-${key}`} className="list-disc pl-5 space-y-1.5 my-3">
        {bullets.map((b, i) => (
          <li key={i} className="text-[#d0d0d0] text-sm leading-relaxed">
            {inline(b, `li-${key}-${i}`)}
          </li>
        ))}
      </ul>,
    );
    bullets = [];
  };

  lines.forEach((raw, i) => {
    const line = raw.trim();
    const key = String(i);

    if (line.startsWith('- ')) {
      bullets.push(line.slice(2));
      return;
    }
    flushBullets(key);

    if (!line) {
      out.push(<div key={`sp-${key}`} className="h-2" />);
    } else if (line.startsWith('## ')) {
      out.push(
        <h3 key={key} className="text-white font-bold text-base mt-6 mb-2">
          {line.slice(3)}
        </h3>,
      );
    } else if (line.startsWith('# ')) {
      out.push(
        <h2 key={key} className="text-white font-bold text-xl mt-2 mb-3">
          {line.slice(2)}
        </h2>,
      );
    } else {
      out.push(
        <p key={key} className="text-[#d0d0d0] text-sm leading-relaxed">
          {inline(line, key)}
        </p>,
      );
    }
  });

  flushBullets('end');
  return <>{out}</>;
}
